import i18n from '@/locale'
import { getGlobalTableConfig, saveGlobalTableConfig, getAliyunToken, getOssHost } from '@/api/config'
import ElementUI, { Message, MessageBox } from 'element-ui'
import langCN from 'element-ui/lib/locale/lang/zh-CN'
import langEN from 'element-ui/lib/locale/lang/en'
import langID from 'element-ui/lib/locale/lang/id'
import langTH from 'element-ui/lib/locale/lang/th'
import locale from 'element-ui/lib/locale'
import { VXETable, Table, Column, Footer, Keyboard, Edit, Tooltip } from 'vxe-table'
import XEUtils from 'xe-utils'
import zhCN from 'vxe-table/lib/locale/lang/zh-CN'
import enUS from 'vxe-table/lib/locale/lang/en-US'
import MGUI from '@/mg-ui/src/main'
import EventBus from './event-bus'
import { tokenName } from '@/config'
import { salt } from '@/config'
import md5 from 'crypto-js/md5'
import bigjs from 'big.js'
import { getStorage, setStorage, LocaleStorage, SettingStorage } from '@/utils/storage'
import Mousetrap from 'mousetrap'
import router from '@/router'
import Speech from 'speak-tts'
import { ipc } from '@/utils/ipcRenderer'
import ipcApiRoute from '@/constants/ipcApiRoute'
import { JsonDBKey } from '@/constants'
import { shortcutManager, ShortcutLayer } from '@/utils/shortcut'
let speech = new Speech()

/**
 * MessageBox（$mgConfirm/$mgAlert）不走 ShortcutManager。
 * 新收银有启用中的中心 scope 时，临时压 exclusive dialog 挡住页级键。
 *
 * 必须在 beforeClose 就 release：response 拦截器里 MessageBox.close() 会把
 * currentMsg 置空，导致 Promise 永不 settle，守卫永久挡住快捷键。
 */
let messageBoxShortcutGuardSeq = 0
function runWithShortcutCenterMessageBoxGuard(openMessageBox) {
  const hasActiveCenter = shortcutManager.stack.some((id) => {
    const scope = shortcutManager.scopes.get(id)
    return !!(scope && scope.enabled)
  })
  if (!hasActiveCenter) {
    return openMessageBox({})
  }

  const scopeId = `message-box-${++messageBoxShortcutGuardSeq}`
  let released = false
  const release = () => {
    if (released) return
    released = true
    try {
      shortcutManager.unregister(scopeId)
    } catch (error) {
      console.error('TIPS message-box shortcut guard release failed:', error)
    }
  }

  shortcutManager.register({
    id: scopeId,
    layer: ShortcutLayer.DIALOG,
    exclusive: true,
    keys: {},
    vm: null,
  })
  shortcutManager.activate(scopeId)

  return Promise.resolve()
    .then(() =>
      openMessageBox({
        beforeClose(action, instance, done) {
          release()
          done()
        },
      })
    )
    .then(
      (value) => {
        release()
        return value
      },
      (error) => {
        release()
        throw error
      }
    )
}

const ElementUILocale = {
  'zh-CN': langCN,
  'en-US': langEN,
  'th-TH': langTH,
  id: langID,
}
const VxeTableLocale = {
  'zh-CN': zhCN,
  'en-US': enUS,
  'th-TH': enUS,
  id: enUS,
}

const QUERY_BUTTON_TEXT = '查询'
const RESET_BUTTON_TEXT = '重置'

function normalizeButtonText(text) {
  return String(text || '').replace(/\s+/g, '')
}

function applyQueryResetButtonClass(root = document.body) {
  if (!root || !root.querySelectorAll) return
  const buttonList = root.querySelectorAll('.el-button')
  buttonList.forEach((buttonEl) => {
    const text = normalizeButtonText(buttonEl.innerText || buttonEl.textContent)
    const isQueryButton = text === QUERY_BUTTON_TEXT
    const isResetButton = text === RESET_BUTTON_TEXT

    if (isQueryButton) {
      buttonEl.classList.add('mg-query-btn')
      buttonEl.classList.remove('mg-reset-btn')
      return
    }
    if (isResetButton) {
      buttonEl.classList.add('mg-reset-btn')
      buttonEl.classList.remove('mg-query-btn')
      return
    }

    buttonEl.classList.remove('mg-query-btn')
    buttonEl.classList.remove('mg-reset-btn')
  })
}

const install = (Vue) => {
  let buttonStyleRafId = null
  const scheduleApplyQueryResetButtonClass = () => {
    if (typeof window === 'undefined') return
    if (buttonStyleRafId) return
    buttonStyleRafId = window.requestAnimationFrame(() => {
      buttonStyleRafId = null
      applyQueryResetButtonClass(document.body)
    })
  }

  const lang = LocaleStorage.get()
  locale.use(ElementUILocale[lang])

  /** ElementUI **/
  Vue.use(ElementUI, { size: 'small' })
  /** VXETable */
  VXETable.setup({
    i18n: (key, args) => XEUtils.toFormatString(XEUtils.get(VxeTableLocale[lang], key), args),
  })
  Vue.use(Column).use(Footer).use(Keyboard).use(Edit).use(Tooltip).use(Table)
  /** MgUI **/
  Vue.use(MGUI, {
    i18n,
    table: {
      getColumnApi: getGlobalTableConfig,
      setColumnApi: saveGlobalTableConfig,
    },
    utils: {
      getOssHost: getOssHost,
      getAliyunToken: getAliyunToken,
      tokenName: tokenName,
      salt: salt,
      md5: md5,
    },
    dropdownButton: {
      getStorage,
      setStorage(key, data) {
        setStorage(key, data.printTemplateId)
      },
    },
    // 通用本地缓存配置
    storage: {
      getStorage,
      setStorage,
    },
    BN: bigjs,
    Mousetrap,
    searchForm: {
      // 列表页面筛选项全局日期校验
      checkDateRange(formConfig, formParams) {
        // 根据路由 name 排除以下单据
        const excluding = [
          'warehouseLocation',
          'WarehousePropertiesList',
          'skubom',
          'goodsOperatAttr',
          'ReturnPoint',
          'noRebateGoods',
          'noDiscountGoods',
          'CleanRecordQuery',
          'paymentSlipAdd',
          'paymentSlipEdit',
          'AddReceiptVoucher',
          'EditReceiptVoucher',
          'GoodsInfo',
        ]

        return new Promise((resolve, reject) => {
          if (excluding.includes(router.currentRoute.name)) {
            resolve()
            return
          }

          // 过滤筛选项中的日期范围类型
          const dateRangeForm = formConfig
            .filter((item) => item.type === 'date' && item?.attrs?.type === 'daterange')
            .map((form) => form.model)

          if (dateRangeForm.length) {
            // 创建一个校验用的数组
            const checkArray = dateRangeForm.map((model) => formParams[model]?.toString())
            const valid = checkArray.some((item) => item)

            // 查询时判断不全为空即可
            if (!valid) {
              MessageBox.confirm(i18n.t('请至少选择一项时间范围'), i18n.t('提示'), {
                confirmButtonText: i18n.t('确定[ENTER]'),
                showCancelButton: false,
                type: 'warning',
              })
              reject()
              return
            }
          }

          resolve()
        })
      },
    },
  })

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    // 统一全局“查询/重置”按钮样式，避免页面逐个改造
    const buttonStyleObserver = new MutationObserver(() => {
      scheduleApplyQueryResetButtonClass()
    })

    Vue.mixin({
      mounted() {
        this.$nextTick(() => {
          scheduleApplyQueryResetButtonClass()
        })
      },
      updated() {
        this.$nextTick(() => {
          scheduleApplyQueryResetButtonClass()
        })
      },
    })

    router.afterEach(() => {
      scheduleApplyQueryResetButtonClass()
    })

    buttonStyleObserver.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    })
    scheduleApplyQueryResetButtonClass()
  }

  /* 全局消息弹框 */
  Vue.prototype.$mgMessage = function (message, type) {
    Message({
      offset: 60,
      showClose: true,
      message: message,
      type: type,
      dangerouslyUseHTMLString: true,
      duration: 1200,
    })
  }

  /* 全局 mgConfirm */
  Vue.prototype.$mgConfirm = function (content, title, options = {}) {
    const userBeforeClose = options.beforeClose
    return runWithShortcutCenterMessageBoxGuard((guardHooks) =>
      MessageBox.confirm(content, title, {
        confirmButtonText: i18n.t('确定[Enter]'),
        cancelButtonText: i18n.t('取消[Ecs]'),
        showCancelButton: true,
        type: 'warning',
        center: true,
        closeOnClickModal: false,
        closeOnPressEscape: true,
        ...options,
        beforeClose(action, instance, done) {
          // 先卸快捷键守卫，再跑业务 beforeClose（避免 MessageBox.close 挂死 Promise）
          if (typeof guardHooks.beforeClose === 'function') {
            guardHooks.beforeClose(action, instance, () => {})
          }
          if (typeof userBeforeClose === 'function') {
            userBeforeClose(action, instance, done)
          } else {
            done()
          }
        },
      })
    )
  }

  /* 全局 mgAlert */
  Vue.prototype.$mgAlert = function (content, title, options = {}) {
    const userBeforeClose = options.beforeClose
    return runWithShortcutCenterMessageBoxGuard((guardHooks) =>
      MessageBox.confirm(content, title, {
        confirmButtonText: i18n.t('确定[Enter]'),
        cancelButtonText: i18n.t('取消[Ecs]'),
        closeOnClickModal: false,
        type: 'warning',
        center: true,
        ...options,
        beforeClose(action, instance, done) {
          if (typeof guardHooks.beforeClose === 'function') {
            guardHooks.beforeClose(action, instance, () => {})
          }
          if (typeof userBeforeClose === 'function') {
            userBeforeClose(action, instance, done)
          } else {
            done()
          }
        },
      })
    )
  }

  /**
   * 编辑的页面确认关闭弹框
   * @param {String} routeName 路由名称
   */
  Vue.prototype.$mgCloseEditPageConfirm = function (routeName) {
    const changePages = this.$store.getters['tabsBar/changePages']
    if (changePages.includes(routeName)) {
      return this.$mgConfirm(i18n.t('当前页面正在编辑，确认关闭?'))
    }
  }

  Vue.prototype.$pxToRem = function (px) {
    return BN.div(1, 16).times(parseFloat(px)).toFixed(4) + 'rem'
  }
  /**
   * 语音播报方法
   * @param text {播报文案}
   * @param source {来源：takeaway(外卖播报) cashier(收银播报) recharge(充值) ......}
   */
  Vue.prototype.$speek = async function (text, source = '') {
    // console.log('TIPS1 speek ready:',source)
    if (source !== 'takeaway') {
      let ifSendVoice = true // 未找到配置时默认开启语音播报
      try {
        let setting = null
        if (ipc?.invoke) {
          const res = await ipc.invoke(ipcApiRoute.dbExec, {
            action: 'query',
            key: JsonDBKey.PRINT_SETTING,
          })
          setting = res?.result?.[0] ?? SettingStorage.get()
        } else {
          setting = SettingStorage.get()
        }
        if (setting != null) {
          ifSendVoice = setting.ifSendVoice ?? true
        }
      } catch (error) {
        // console.error('TIPS1 $speek query PRINT_SETTING error:', error)
        const setting = SettingStorage.get()
        if (setting != null) {
          ifSendVoice = setting.ifSendVoice ?? true
        }
      }
      if (!ifSendVoice) return
    }
    // console.log('TIPS1 speek do:',speech)
    if (!speech) {
      speech = new Speech()
      speech.setVolume(1)
    } else {
      speech.setVolume(1)
    }

    if (!speech.hasBrowserSupport()) {
      this.$mgMessage(i18n.t('当前系统不支持语音播报功能,请联系系统管理员!'), 'warning')
      return Promise.reject(new Error(i18n.t('当前系统不支持语音播报功能')))
    }
    speech.setLanguage(LocaleStorage.get())
    return speech.init().then(() => {
      // console.log('TIPS1 speek do2:',text)
      return speech.speak({ text })
    })
  }

  /**
   * 全局格式化
   */
  Vue.prototype.$format = {
    /**
     * 千分法金额格式化
     * @param {number} value
     * @returns
     */
    amount: (value) => {
      if (!value) return value
      return value.toString().replace(/\d+/, (n) => {
        return n.replace(/(\d)(?=(\d{3})+$)/g, '$1,')
      })
    },
    /**
     * 【日期】格式化
     * @param {*} date 有效日期数值
     * @param {*} format 默认 YYYY-MM-DD
     * @returns
     */
    date(date, format = 'YYYY-MM-DD') {
      if (!date) return '--'
      return dayjs(date).format(format)
    },
    /**
     * 【日期+时间】格式化
     * @param {*} date 有效日期数值
     * @param {*} format 默认 YYYY-MM-DD HH:mm:ss
     * @returns
     */
    datetime(date, format = 'YYYY-MM-DD HH:mm:ss') {
      if (!date) return '--'
      return dayjs(date).format(format)
    },
    /**
     * 格式化表格【日期】列(YYYY-MM-DD)
     * @param {*} row
     * @param {*} column
     * @param {*} value
     * @returns
     */
    dateColumn(row, column, value) {
      if (!value) return '--'
      return dayjs(value).format('YYYY-MM-DD')
    },
    /**
     * 格式化表格【日期+时间】列(YYYY-MM-DD HH:mm:ss)
     * @param {} row
     * @param {*} column
     * @param {*} value
     * @returns
     */
    datetimeColumn(row, column, value) {
      if (!value) return '--'
      return dayjs(Number(value)).format('YYYY-MM-DD HH:mm:ss')
    },
  }

  // dayjs 通过 ProvidePlugin 全局引入
  Vue.prototype.dayjs = dayjs
  window.$dayjs = dayjs
  // BN 通过 ProvidePlugin 全局引入
  Vue.prototype.BN = BN
  // 全局 EventBus
  Vue.prototype.$GlobalEventBus = EventBus
}

if (typeof window !== void 0 && window.Vue) {
  install(window.Vue)
}

export default install
